import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Checkbox from 'common/components/atoms/Checkbox';
import { Table, Th } from 'common/components/organisms/DataTable';
import { KEY_COLUMN } from '../../machineCsv.constants';
import ColumnTableRow from './ColumnTableRow';
import {
  columnLabel, columnSelection, csvHeaderOf, selectableKeys
} from './helpers';
import { HeaderCell, TableScroll } from './MachineCsv.styled';

const noop = () => {};

// One table of every column: tick to export it, and whether an import can
// change it. The header box selects or clears every column but Machine ID.
function ColumnTable({
  columns, selected, onToggle, onSetAll
}) {
  const { t } = useTranslation();
  const { all, some } = columnSelection(columns, selected);
  const toggleAll = useCallback(() => onSetAll(selectableKeys(columns), !all), [onSetAll, columns, all]);
  const yes = t('common.yes', { defaultValue: 'Yes' });
  const no = t('common.no', { defaultValue: 'No' });

  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <Th>
              <HeaderCell>
                <Checkbox
                  id="csv-column-all"
                  value={all}
                  minus={some && !all}
                  onChange={toggleAll}
                />
                {t('assets.csv.export.columnHeader', { defaultValue: 'Column' })}
              </HeaderCell>
            </Th>
            <Th>{t('assets.csv.export.editableHeader', { defaultValue: 'Editable' })}</Th>
          </tr>
        </thead>
        <tbody>
          {columns.map((column) => {
            const isKey = column.key === KEY_COLUMN;
            return (
              <ColumnTableRow
                key={column.key}
                columnKey={column.key}
                label={columnLabel(t, column.key)}
                csvHeader={csvHeaderOf(column)}
                checked={isKey || selected.includes(column.key)}
                locked={isKey}
                lockedHint={isKey ? t('assets.csv.export.alwaysIncluded', { defaultValue: 'Always included' }) : null}
                editable={column.editable}
                editableLabel={column.editable ? yes : no}
                onToggle={isKey ? noop : onToggle}
              />
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
}

ColumnTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
    editable: PropTypes.bool
  })).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSetAll: PropTypes.func.isRequired
};

export default ColumnTable;
